import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAxios } from 'hooks';
import { endPoints } from 'api/endpoints';
import { useRecoilState } from 'recoil';
import { useParams } from 'react-router-dom';
import { catalogs } from 'store';
import ProvisioningStyles from 'pages/provisioning/vm/style';
import {
    SecondaryApproverBanner,
    environmentDetails,
    isMandatoryTagsFullfilled,
    orderId,
    sspSubmitOrder,
    toggleSspModels,
} from 'pages/provisioning/aws/kafka-topic/store';
import { fetchAllMembers } from 'pages/provisioning/kafka/utils/fetchMembers';
import { resetOrder, resetToggleSspModal, formOrderObjFromCart, formOrderObjFromOrderDetails } from './utils/index';

const ProvisioningHeader = React.lazy(() => import('pages/provisioning/aws/kafka-topic/ProvisioningHeader'));
const ApplicationDetails = React.lazy(() => import('pages/provisioning/aws/kafka-topic/ApplicationDetails'));
const SspForm = React.lazy(() => import('pages/provisioning/aws/kafka-topic/SspForm'));
const AddedToCartConfirmModal = React.lazy(() => import('pages/provisioning/aws/kafka-topic/AddedToCartConfirmModal'));
const UpdatedToCartConfirmModal = React.lazy(() => import('pages/provisioning/aws/kafka-topic/UpdatedToCartConfirmModal'));
const ShowCostDetails = React.lazy(() => import('pages/provisioning/aws/kafka-topic/ShowCostDetails'));
const CustomSnackBar = React.lazy(() => import('pages/provisioning/kafka/components/CustomSnackBar'));
const Tags = React.lazy(() => import('pages/provisioning/aws/kafka-topic/Tags'));

const AwsKafkaTopicProvisioningPage = () => {
    const [isLoading, setLoading] = React.useState(true);
    const [cmpCatalogs] = useRecoilState(catalogs);
    const [order, setOrder] = useRecoilState(sspSubmitOrder);
    const [, resetOrderId] = useRecoilState(orderId);
    const [, resetToggleSspModels] = useRecoilState(toggleSspModels);
    const [, setEnvironmentDetails] = useRecoilState(environmentDetails);
    const [, setIsMandatoryTags] = useRecoilState(isMandatoryTagsFullfilled);
    const [showSnackBar, setShowSnackBar] = useRecoilState(SecondaryApproverBanner);
    const { catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const axios = useAxios();

    async function getCosts() {
        const { data } = await axios.get(`${endPoints.catalog.get}/${catalogCode}/costs`);
        return data;
    }

    async function checkCatalog() {
        const data = cmpCatalogs?.find((item) => item?.catalogDefinitionId === catalogCode);
        const costs = await getCosts();
        if (cartItemId && operation && !catalogServiceId) {
            const { data: cartItem } = await axios.get(`${endPoints.cart.getCartItem}/${cartItemId}`);
            const orderObj = await formOrderObjFromCart(cartItem, data?.images?.sku);

            // Fetch adGroupOwner details if activeDirectoryGroupOwnerId exists in catalogService
            const adGroupOwner = cartItem?.itemDetail?.catalogService?.activeDirectoryGroupOwnerId
                ? await fetchAllMembers([cartItem?.itemDetail?.catalogService?.activeDirectoryGroupOwnerId])
                : null;

            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                sku: data?.images?.sku,
                emptyCost: costs,
                ...(adGroupOwner && {
                    adGroupOwner: `${adGroupOwner?.[0]?.gpid} - ${adGroupOwner?.[0]?.name}`,
                    adGroupOwnerDetails: {
                        label: `${adGroupOwner?.[0]?.gpid} - ${adGroupOwner?.[0]?.name}`,
                        gpid: `${adGroupOwner?.[0]?.gpid}`,
                        name: `${adGroupOwner?.[0]?.name}`,
                        email: `${adGroupOwner?.[0]?.email}`,
                    },
                }),
            }));
        }
        if (cartItemId && operation && catalogServiceId) {
            const { data: orderDetails } = await axios.get(`${endPoints.order.orderDetails}/${cartItemId}`);
            const orderObj = formOrderObjFromOrderDetails(catalogServiceId, orderDetails, data?.images?.sku);

            // Fetch adGroupOwner details if activeDirectoryGroupOwnerId exists
            const catalog = orderDetails?.catalogServices?.find((item) => item?.catalogServiceId === catalogServiceId) || {};
            const adGroupOwner = catalog?.activeDirectoryGroupOwnerId ? await fetchAllMembers([catalog?.activeDirectoryGroupOwnerId]) : null;

            setOrder((previous) => ({
                ...previous,
                ...orderObj,
                sku: data?.images?.sku,
                ...(adGroupOwner && {
                    adGroupOwner: `${adGroupOwner?.[0]?.gpid} - ${adGroupOwner?.[0]?.name}`,
                    adGroupOwnerDetails: {
                        label: `${adGroupOwner?.[0]?.gpid} - ${adGroupOwner?.[0]?.name}`,
                        gpid: `${adGroupOwner?.[0]?.gpid}`,
                        name: `${adGroupOwner?.[0]?.name}`,
                        email: `${adGroupOwner?.[0]?.email}`,
                    },
                }),
            }));
        } else {
            setOrder((previous) => ({
                ...previous,
                sku: data?.images?.sku,
                catalogCode,
                catalogType: data?.displayName,
                cloudProvider: data?.cloudProvider,
                cloudService: data?.cloudService,
                validationStatus: 'clearValidation',
                isTouched: false,
                emptyCost: costs,
            }));
        }
        setLoading(false);
    }

    const reset = () => {
        setOrder({ ...order, ...resetOrder });
        resetOrderId('');
        resetToggleSspModels(resetToggleSspModal);
        setEnvironmentDetails({
            Application_Manager: '---',
            Project: '---',
        });
        setIsMandatoryTags(false);
        setShowSnackBar({ status: false, message: null });
    };

    const handleCustomSnackbar = () => {
        setShowSnackBar({ status: false, message: null });
    };

    React.useEffect(() => {
        if (cmpCatalogs?.length && catalogCode) {
            setLoading(true);
            reset();
            checkCatalog();
        }
    }, [cmpCatalogs, catalogCode]);

    /* eslint-disable arrow-body-style */
    React.useEffect(() => {
        return () => reset();
        // eslint-disable-next-line
    }, []);

    return (
        <>
            <Helmet>
                <title>AWS Kafka Topic Provisioning</title>
            </Helmet>
            <Suspense
                fallback={
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                }
            >
                {isLoading && (
                    <Box sx={ProvisioningStyles.provisioning_wrapper}>
                        <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
                        <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                            Loading
                        </Typography>
                    </Box>
                )}
                {!isLoading && (
                    <Box sx={ProvisioningStyles.provisioningBox}>
                        <ProvisioningHeader />
                        <Box sx={ProvisioningStyles.sspform_wrapper} className="contentContainer">
                            <SspForm />
                        </Box>
                        <br />
                        <ApplicationDetails />
                        <Tags />
                        <AddedToCartConfirmModal />
                        <UpdatedToCartConfirmModal />
                        <ShowCostDetails />
                        <CustomSnackBar show={showSnackBar?.status} message={showSnackBar?.message} handleCloseCallback={handleCustomSnackbar} />
                    </Box>
                )}
            </Suspense>
        </>
    );
};

export default React.memo(AwsKafkaTopicProvisioningPage);
